/**
 * Player Image Store
 *
 * Manages player-uploaded images. Uploads go directly to Supabase Storage,
 * metadata tracked via tRPC API. Client-side resizing via canvas element.
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { trpc } from '../lib/trpc';
import type { PlayerImage, ImagePurpose } from '@machina/core';

// =============================================================================
// RESIZE HELPERS
// =============================================================================

const MAX_SIZES: Record<ImagePurpose, number> = {
  token: 256,
  profile: 512,
  general: 512,
};

function resizeImage(file: File, maxSize: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas toBlob failed'));
        },
        'image/webp',
        0.85
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

// =============================================================================
// STORE
// =============================================================================

interface PlayerImageState {
  images: PlayerImage[];
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;

  loadImages: (purpose?: ImagePurpose) => Promise<void>;
  uploadImage: (file: File, purpose: ImagePurpose, userId: string) => Promise<PlayerImage>;
  deleteImage: (image: PlayerImage) => Promise<void>;
}

export const usePlayerImageStore = create<PlayerImageState>()((set) => ({
  images: [],
  isLoading: false,
  isUploading: false,
  error: null,

  loadImages: async (purpose) => {
    set({ isLoading: true, error: null });
    try {
      const images = await trpc.images.list.query(purpose ? { purpose } : undefined);
      set({ images, isLoading: false });
    } catch (err) {
      console.error('[playerImageStore] Failed to load images:', err);
      set({ isLoading: false, error: 'Failed to load images' });
    }
  },

  uploadImage: async (file, purpose, userId) => {
    set({ isUploading: true, error: null });
    try {
      // 1. Resize client-side
      const maxSize = MAX_SIZES[purpose];
      const resized = await resizeImage(file, maxSize);

      // 2. Build storage path: {userId}/{purpose}/{timestamp}.webp
      const timestamp = Date.now();
      const storagePath = `${userId}/${purpose}/${timestamp}.webp`;

      // 3. Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('player-images')
        .upload(storagePath, resized, {
          contentType: 'image/webp',
          upsert: false,
        });

      if (uploadError) {
        throw new Error(`Storage upload failed: ${uploadError.message}`);
      }

      // 4. Get public URL
      const { data: urlData } = supabase.storage
        .from('player-images')
        .getPublicUrl(storagePath);

      const publicUrl = urlData.publicUrl;

      // 5. Get dimensions from the resized blob
      const dimensions = await getImageDimensions(resized);

      // 6. Save metadata via API
      const playerImage = await trpc.images.saveMetadata.mutate({
        storagePath,
        publicUrl,
        originalName: file.name,
        purpose,
        width: dimensions.width,
        height: dimensions.height,
        sizeBytes: resized.size,
        mimeType: 'image/webp',
      });

      // 7. Add to local state
      set((state) => ({
        images: [playerImage, ...state.images],
        isUploading: false,
      }));

      return playerImage;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      console.error('[playerImageStore] Upload error:', msg);
      set({ isUploading: false, error: msg });
      throw err;
    }
  },

  deleteImage: async (image) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('player-images')
        .remove([image.storagePath]);

      if (storageError) {
        console.warn('[playerImageStore] Storage delete failed:', storageError.message);
      }

      // Delete metadata
      await trpc.images.delete.mutate({ id: image.id });

      // Remove from local state
      set((state) => ({
        images: state.images.filter((i) => i.id !== image.id),
      }));
    } catch (err) {
      console.error('[playerImageStore] Delete error:', err);
      set({ error: 'Failed to delete image' });
    }
  },
}));

// Helper to get dimensions from a blob
function getImageDimensions(blob: Blob): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}
